/**
 * Friendly "click to enable audio" overlay for Anagram. Mirrors the WhatIs
 * pattern. Hidden inside OBS (page-permissions usually unlocks audio without
 * a gesture).
 */

import { audioBus } from '../audio/bus.js';
import { music } from '../audio/music.js';

const OVERLAY_ID = 'an-audio-unlock';

function isObs(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { obsstudio?: unknown }).obsstudio) return true;
  if (typeof navigator !== 'undefined' && /OBS\//i.test(navigator.userAgent)) return true;
  return false;
}

function ensureStyles(): void {
  if (document.getElementById('an-audio-unlock-style')) return;
  const s = document.createElement('style');
  s.id = 'an-audio-unlock-style';
  s.textContent = `
    #${OVERLAY_ID} {
      position: fixed;
      bottom: 12px;
      left: 12px;
      z-index: 9998;
      padding: 8px 14px;
      background: rgba(0, 0, 0, 0.78);
      color: var(--sg-primary, #FFC43D);
      border: 1px solid rgba(255, 196, 61, 0.4);
      border-radius: 8px;
      font: 600 13px/1.2 'Inter', system-ui, sans-serif;
      letter-spacing: 0.04em;
      cursor: pointer;
      box-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
    }
    #${OVERLAY_ID}[hidden] { display: none; }
  `;
  document.head.appendChild(s);
}

export function mountAudioUnlock(): HTMLButtonElement | null {
  if (typeof document === 'undefined') return null;
  if (isObs()) {
    void audioBus.unlock();
    return null;
  }
  if (audioBus.isUnlocked()) return null;

  ensureStyles();
  const btn = document.createElement('button');
  btn.id = OVERLAY_ID;
  btn.type = 'button';
  btn.textContent = 'Click to enable audio';
  btn.setAttribute('data-testid', 'an-audio-unlock');

  async function tryUnlock(): Promise<void> {
    await audioBus.unlock();
    if (audioBus.isUnlocked()) {
      btn.hidden = true;
      music.resumeIfPending();
      cleanup();
    }
  }

  function onAnyGesture(): void {
    void tryUnlock();
  }

  btn.addEventListener('click', () => { void tryUnlock(); });
  document.addEventListener('pointerdown', onAnyGesture, { once: false });
  document.addEventListener('keydown', onAnyGesture, { once: false });

  function cleanup(): void {
    document.removeEventListener('pointerdown', onAnyGesture);
    document.removeEventListener('keydown', onAnyGesture);
  }

  document.body.appendChild(btn);
  void tryUnlock();
  return btn;
}
